import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AIOPEN_API = 'https://aiopen-api.openkontakt.com/api/v1';
const API = '/api';
// Dedykowana instancja axios bez globalnych naglowkow (np. Authorization mailmanagera),
// ktore nie powinny byc wysylane do AI OPEN - to byla przyczyna 404 przy logowaniu.
const aiOpenAxios = axios.create();
const TOKEN_REFRESH_MS = 7 * 60 * 60 * 1000; // 7h, identycznie jak w AI OPEN

export default function LiveAlertBar({ currentUser, onOpenAiOpen, onOpenReturns }: { currentUser: any; onOpenAiOpen: () => void; onOpenReturns: () => void }) {
  const [token, setToken] = useState('');
  const [waitingConvsCount, setWaitingConvsCount] = useState(0);
  const [aiConvsCount, setAiConvsCount] = useState(0);
  const [returnConvsCount, setReturnConvsCount] = useState(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [returnAcceptAlertCount, setReturnAcceptAlertCount] = useState(0);
  const [paymentNoticeActive, setPaymentNoticeActive] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [payingPlan, setPayingPlan] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const buildHashRef = useRef<string | null>(null);
  const refreshTimer = useRef<any>(null);
  const pollRef = useRef<any>(null);
  const returnsPollRef = useRef<any>(null);
  const paymentPollRef = useRef<any>(null);
  const ordersPollRef = useRef<any>(null);
  const isAdmin = currentUser?.role === 'admin';

  const companyId = currentUser?.company_id || 'no-company-id';
  // modules.includes('aiopen') to tylko widocznosc menu w UI, NIE potwierdzenie ze firma
  // faktycznie ma aktywne konto AI OPEN po stronie tamtego serwisu. Jesli logowanie raz
  // zawiedzie (404), zapamietujemy to per-firma w sessionStorage i nie probujemy ponownie
  // w tej sesji przegladarki - zapobiega to powtarzajacym sie bledom w konsoli.
  const unavailableKey = `aiopen_unavailable_${companyId}`;
  const [aiOpenUnavailable, setAiOpenUnavailable] = useState(() => sessionStorage.getItem(unavailableKey) === '1');
  const hasAiOpen = (currentUser?.modules || []).includes('aiopen') && !aiOpenUnavailable && !!currentUser?.company_id;

  const doLogin = useCallback(async () => {
    if (!hasAiOpen) return;
    try {
      const res = await aiOpenAxios.post(`${AIOPEN_API}/operators/login`, {
        openkontakt_company_id: currentUser?.company_id,
        openkontakt_user_id: currentUser?.id || currentUser?.login || 'user',
        name: currentUser?.name || currentUser?.login || 'Operator',
        email: currentUser?.email || '',
      });
      setToken(res.data.token);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => doLogin(), TOKEN_REFRESH_MS);
    } catch (e: any) {
      // Reset wszystkich liczników AI OPEN - stare dane nie moga byc wyswietlane
      // gdy logowanie zawiodlo (np. modul zdezaktywowany po tym jak wczesniej dzialal)
      setToken('');
      setWaitingConvsCount(0);
      setAiConvsCount(0);
      setReturnConvsCount(0);
      setPendingOrdersCount(0);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      if (pollRef.current) clearInterval(pollRef.current);
      if (e?.response?.status === 404) {
        sessionStorage.setItem(unavailableKey, '1');
        setAiOpenUnavailable(true);
      } else {
        console.error('[LiveAlertBar] Błąd logowania AI OPEN', e);
      }
    }
  }, [currentUser, hasAiOpen, unavailableKey]);

  useEffect(() => {
    if (!hasAiOpen) return;
    doLogin();
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [doLogin, hasAiOpen]);

  useEffect(() => {
    if (!token || !hasAiOpen) return;
    const poll = async () => {
      try {
        const h = { Authorization: `Bearer ${token}` };
        const cr = await aiOpenAxios.get(`${AIOPEN_API}/conversations`, { headers: h, params: { limit: 100 } });
        const convs: any[] = cr.data?.conversations || [];
        const LIVE_WINDOW_MS = 2 * 60 * 1000;
        const isLive = (ts: string | null | undefined) => { if (!ts) return false; return (Date.now() - new Date(ts).getTime()) < LIVE_WINDOW_MS; };
        setWaitingConvsCount(convs.filter((c: any) => c.status === 'waiting').length);
        setAiConvsCount(convs.filter((c: any) => c.status !== 'closed' && isLive(c.last_message_at)).length);
        setReturnConvsCount(convs.filter((c: any) => c.is_return).length);
      } catch (e) {
        console.error('[LiveAlertBar] Poll', e);
      }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [token, hasAiOpen]);
  // Trwaly monit zamowien do przeslania (status=new, nieprzeslane do BaseLinker/Apilo).
  // Nie ma okna czasowego - wisi dopoki zamowienie nie zostanie przeslane lub zamkniete.
  useEffect(() => {
    if (!token || !hasAiOpen) return;
    const pollOrders = async () => {
      try {
        const h = { Authorization: `Bearer ${token}` };
        const res = await aiOpenAxios.get(`${AIOPEN_API}/leads/pending-export-count`, { headers: h });
        setPendingOrdersCount(res.data?.count || 0);
      } catch (e) {
        console.error('[LiveAlertBar] Poll orders', e);
      }
    };
    pollOrders();
    ordersPollRef.current = setInterval(pollOrders, 5000);
    return () => clearInterval(ordersPollRef.current);
  }, [token, hasAiOpen]);

  // Niezalezny polling alertu o niezaakceptowanych zwrotach - wlasny backend, nie AI OPEN
  useEffect(() => {
    if (!currentUser) return;
    const pollReturns = async () => {
      try {
        const res = await axios.get(`${API}/returns/accept-alert-count`);
        setReturnAcceptAlertCount(res.data?.enabled ? (res.data?.count || 0) : 0);
      } catch (e) {
        console.error('[LiveAlertBar] Poll returns', e);
      }
    };
    pollReturns();
    returnsPollRef.current = setInterval(pollReturns, 5000);
    return () => clearInterval(returnsPollRef.current);
  }, [currentUser]);

  // Polling powiadomienia o platnosci - tylko dla administratora firmy
  useEffect(() => {
    if (!isAdmin) return;
    const pollPayment = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`);
        setPaymentNoticeActive(!!res.data?.payment_notice_sent_at);
      } catch (e) {
        console.error('[LiveAlertBar] Poll payment', e);
      }
    };
    pollPayment();
    paymentPollRef.current = setInterval(pollPayment, 5000);
    return () => clearInterval(paymentPollRef.current);
  }, [isAdmin]);

  // Wykrywanie nowej wersji frontu: hash bundla main.[hash].js w index.html.
  // index.html jest no-cache (nginx), wiec zawsze swiezy. Pierwszy odczyt = wersja bazowa;
  // kazda pozniejsza zmiana hasha => alert o nowej wersji.
  useEffect(() => {
    let stopped = false;
    const checkVersion = async () => {
      try {
        const res = await fetch(`/index.html?_ts=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        const m = html.match(/main\.([a-z0-9]+)\.js/);
        if (!m) return;
        const hash = m[1];
        if (buildHashRef.current === null) {
          buildHashRef.current = hash;
        } else if (hash !== buildHashRef.current) {
          if (!stopped) setUpdateAvailable(true);
        }
      } catch (e) { /* blad sieci - sprobujemy nastepnym razem */ }
    };
    checkVersion();
    const iv = setInterval(checkVersion, 2 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') checkVersion(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { stopped = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const payWithPlan = async (plan: string) => {
    if (!termsChecked || !privacyChecked) return;
    setPayingPlan(plan);
    try {
      await axios.post(`${API}/subscription/accept-terms`);
      const res = await axios.post(`${API}/subscription/create-checkout`, { plan });
      window.location.href = res.data.url;
    } catch (e) {
      alert('Błąd — spróbuj ponownie');
      setPayingPlan(null);
    }
  };

  const anyActive = aiConvsCount > 0 || pendingOrdersCount > 0 || waitingConvsCount > 0 || returnConvsCount > 0 || returnAcceptAlertCount > 0 || paymentNoticeActive || updateAvailable;
  if (!anyActive) return null;

  return (
    <div style={{ padding: '4px 14px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' as const }}>
        {updateAvailable && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderRadius: 8, background: '#dbeafe', border: '1px solid #93c5fd' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0, background: '#2563eb', boxShadow: '0 0 0 2px #bfdbfe' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#1e40af' }}>Dostępna nowa wersja aplikacji</span>
            <button onClick={() => window.location.reload()} style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#2563eb', border: 'none', borderRadius: 6, padding: '3px 12px', cursor: 'pointer' }}>Odśwież</button>
          </div>
        )}
      {aiConvsCount > 0 && (
        <div onClick={onOpenAiOpen}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none' as const,
            background: '#dcfce7', transition: 'background 0.3s' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            background: '#16a34a', boxShadow: '0 0 0 2px #bbf7d0' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d' }}>
            Rozmowa ({aiConvsCount})
          </span>
        </div>
      )}
      {pendingOrdersCount > 0 && (
        <div onClick={onOpenAiOpen}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none' as const,
            background: '#dcfce7', transition: 'background 0.3s' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            background: '#16a34a', boxShadow: '0 0 0 2px #bbf7d0' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d' }}>
            Zamówienie{pendingOrdersCount > 1 ? ` (${pendingOrdersCount})` : ''}
          </span>
        </div>
      )}
      {waitingConvsCount > 0 && (
        <div onClick={onOpenAiOpen}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none' as const,
            background: '#fee2e2', transition: 'background 0.3s' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            background: '#dc2626' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>
            Czeka na operatora ({waitingConvsCount})
          </span>
        </div>
      )}
      {returnConvsCount > 0 && (
        <div onClick={onOpenAiOpen}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none' as const,
            background: '#fee2e2', transition: 'background 0.3s' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            background: '#dc2626' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>
            Zgłoszono Zwrot ({returnConvsCount})
          </span>
        </div>
      )}
      {returnAcceptAlertCount > 0 && (
        <div onClick={onOpenReturns}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none' as const,
            background: '#fee2e2', transition: 'background 0.3s' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            background: '#dc2626' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>
            Zaakceptuj zwrot ({returnAcceptAlertCount})
          </span>
        </div>
      )}
      {paymentNoticeActive && (
        <div onClick={() => setShowPaymentModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, cursor: 'pointer', userSelect: 'none' as const,
            background: '#fef9c3', transition: 'background 0.3s' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0,
            background: '#d97706' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>
            💳 Płatność wymagana
          </span>
        </div>
      )}
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: 10, color: '#cbd5e1' }}>co 5s</span>
      {showPaymentModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowPaymentModal(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480, maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto' as const, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 16px', marginBottom: 18, fontSize: 13, lineHeight: 1.5, color: '#1e3a5f' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Dziękujemy, że jesteś z nami! 💙</div>
              <div>Cieszymy się, że zaufałeś OPENKontakt i wybrałeś naszą aplikację. Twoja opinia ma dla nas ogromne znaczenie — jeśli masz jakiekolwiek pytania, uwagi lub pomysły, jak moglibyśmy działać jeszcze lepiej i wygodniej, podziel się nimi z nami.</div>
              <div style={{ marginTop: 6 }}>Zależy nam, aby OPENKontakt był maksymalnie prosty w obsłudze, a Ty był w pełni zadowolony z korzystania z niego na co dzień.</div>
            </div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Wybierz plan idealny dla Ciebie</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>Po opłaceniu Twoje dane pozostają bez zmian.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, fontSize: 13 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={termsChecked} onChange={e => setTermsChecked(e.target.checked)} style={{ marginTop: 2 }} />
                <span>Akceptuję <a href="https://openkontakt.com/regulamin" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>Regulamin</a> serwisu OPENkontakt</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={privacyChecked} onChange={e => setPrivacyChecked(e.target.checked)} style={{ marginTop: 2 }} />
                <span>Akceptuję <a href="https://openkontakt.com/polityka-prywatnosci" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>Politykę prywatności</a> serwisu OPENkontakt</span>
              </label>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
              <button disabled={!termsChecked || !privacyChecked || !!payingPlan} onClick={() => payWithPlan('monthly')}
                style={{ padding: '16px', border: '2px solid #e5e7eb', borderRadius: 10, cursor: (!termsChecked || !privacyChecked || !!payingPlan) ? 'not-allowed' : 'pointer', textAlign: 'left' as const, background: '#fff', opacity: (!termsChecked || !privacyChecked) ? 0.5 : (payingPlan && payingPlan !== 'monthly') ? 0.4 : 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{payingPlan === 'monthly' ? 'Przetwarzanie...' : '200 zł / miesiąc'}</div>
                <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>Pełen dostęp do wszystkich funkcji</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Dla tych, którzy wolą elastyczność</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>+ 0,04 zł/ofertę powyżej 5000 · netto</div>
              </button>
              <button disabled={!termsChecked || !privacyChecked || !!payingPlan} onClick={() => payWithPlan('yearly')}
                style={{ padding: '16px', border: '2px solid #e5e7eb', borderRadius: 10, cursor: (!termsChecked || !privacyChecked || !!payingPlan) ? 'not-allowed' : 'pointer', textAlign: 'left' as const, background: '#fff', opacity: (!termsChecked || !privacyChecked) ? 0.5 : (payingPlan && payingPlan !== 'yearly') ? 0.4 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>⭐ {payingPlan === 'yearly' ? 'Przetwarzanie...' : '2000 zł / rok'}</div>
                  <span style={{ background: '#1d4ed8', color: '#fff', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Oszczędzasz 400 zł</span>
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>Pełen dostęp + ok. 17% taniej w skali miesiąca</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>Dla tych, którzy cenią oszczędność</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>+ 0,04 zł/ofertę powyżej 5000 · netto</div>
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 18, lineHeight: 1.4 }}>
              Wybierz roczny plan i oszczędzaj ok. 17% miesięcznie — to najlepsza opcja, jeśli planujesz korzystać z aplikacji dłużej.
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, textAlign: 'center' as const }}>
              Masz pytania przed zakupem? <a href="https://openkontakt.com/kontakt" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>Skontaktuj się z nami</a> — chętnie pomożemy.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: '#9ca3af', marginBottom: 16 }}>
              🔒 Bezpieczna płatność · obsługiwane przez Stripe
            </div>
            <button onClick={() => setShowPaymentModal(false)}
              style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'transparent', cursor: 'pointer', color: '#6b7280', fontSize: 13 }}>
              Anuluj
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
